import { useEffect } from "react";
import { Select, Tag } from "antd";
import { useSearchParams } from "react-router-dom";
import { ListPage } from "../../components/ListPage";
import { fmtItem } from "../../helpers/format";
import type { ReceiptDiscrepancy } from "../../api-models";
import { coveredQty } from "./coverage";

export default function DiscrepanciesListPage() {
  // Open discrepancies are the ones still needing someone to act, so the register opens on
  // them. Seeding the URL rather than holding a default in state keeps the filter shareable
  // and lets "Closed" survive a reload.
  const [params, setParams] = useSearchParams();
  const hasStatus = params.has("status");
  useEffect(() => {
    if (hasStatus) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("status", "OPEN");
        return next;
      },
      { replace: true },
    );
  }, [hasStatus, setParams]);

  return (
    <ListPage<ReceiptDiscrepancy>
      title="Receipt Discrepancies"
      endpoint="/discrepancies"
      searchPlaceholder="Search by item..."
      linkTo={(r) => `/discrepancies/${r.id}`}
      filters={({ value, set }) => (
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 160 }}
          value={value("status")}
          onChange={(v) => set("status", v)}
          options={[
            { value: "OPEN", label: "Open" },
            { value: "CLOSED", label: "Closed" },
          ]}
        />
      )}
      columns={[
        { title: "Code", render: (_, r) => r.item?.code },
        { title: "Item", render: (_, r) => r.item?.name },
        { title: "Source", dataIndex: "sourceType", sorter: true, render: (v: string) => v.replace(/_/g, " ").toLowerCase() },
        { title: "Dispatched", dataIndex: "dispatchedQty", sorter: true },
        {
          title: "Accounted for",
          render: (_, r) => coveredQty(r),
        },
        {
          title: "Status",
          dataIndex: "status",
          sorter: true,
          render: (v: string) => <Tag color={v === "OPEN" ? "orange" : "green"}>{v}</Tag>,
        },
        { title: "Opened", key: "openedAt", sorter: true, render: (_, r) => new Date(r.openedAt).toLocaleDateString() },
      ]}
      renderCard={(r) => (
        <div>
          <strong>{fmtItem(r.item)}</strong>
          <div>
            {r.dispatchedQty} dispatched · {coveredQty(r)} accounted for
          </div>
          <Tag color={r.status === "OPEN" ? "orange" : "green"}>{r.status}</Tag>
        </div>
      )}
    />
  );
}
